import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onIdTokenChanged, signOut, type User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { can, isStaff, DEFAULT_POLICIES, type Permission, type Role, type AcademyPolicies } from '../domain/index.js';

interface Session {
  loading: boolean; user: User | null; roles: Role[]; memberId?: string;
  can: (p: Permission) => boolean; staff: boolean; policies: AcademyPolicies; signOut: () => Promise<void>; refresh: () => Promise<void>;
}
const Ctx = createContext<Session | null>(null);

/** Reads roles/memberId from server-set custom claims. UI checks are convenience; server + rules enforce. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [s, set] = useState<Pick<Session, 'loading' | 'user' | 'roles' | 'memberId' | 'policies'>>({ loading: true, user: null, roles: [], policies: DEFAULT_POLICIES });
  const [denied, setDenied] = useState<Permission[]>([]);
  useEffect(() => onIdTokenChanged(auth, async user => {
    if (!user) return set({ loading: false, user: null, roles: [], policies: DEFAULT_POLICIES });
    const t = await user.getIdTokenResult();
    const roles = (Array.isArray(t.claims.roles) ? t.claims.roles : []) as Role[];
    const pub = await getDoc(doc(db, 'settings', 'public')).catch(() => null);
    const pp = pub?.data()?.policies ?? {};
    const policies = { ...DEFAULT_POLICIES, ...pp, booking: { ...DEFAULT_POLICIES.booking, ...(pp.booking ?? {}) } } as AcademyPolicies;
    if (isStaff(roles)) { const r = await getDoc(doc(db, 'settings', 'roles')).catch(() => null); setDenied(r?.data()?.adminDenied ?? []); }
    set({ loading: false, user, roles, memberId: t.claims.memberId as string | undefined, policies });
  }), []);
  const value: Session = { ...s, can: p => can(s.roles, p, denied), staff: isStaff(s.roles), signOut: () => signOut(auth),
    refresh: async () => { await auth.currentUser?.getIdToken(true); } };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export function useSession() { const v = useContext(Ctx); if (!v) throw new Error('SessionProvider missing'); return v; }
